import { Injectable, OnDestroy } from '@angular/core';
import { Task, TaskScopeEnum, TaskService, TaskStatusEnum } from '@mydocma/task';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, share } from 'rxjs/operators';
import { CoEvent, CoEventService, StructureEventData, StructureReorderEventData } from '@mydocma/co';
import { StructureService } from '@mydocma/co/src/lib/common';

export enum StructureSyncErrorKind {
  BackendFailed = 'backend-failed',
  ResolutionFailed = 'resolution-failed',
}

export interface StructureSyncError {
  kind: StructureSyncErrorKind;
  reason?: string;
  info: Record<string, unknown>;
}

interface BackendTaskPayload {
  schemaId?: string;
  projectStructureId?: string;
  externalId?: number | string;
  structureId?: number | string;
}

interface PendingShell {
  uid: string;
  parentUid?: string;
  name?: string;
  desc?: string;
  level?: number;
  sortId?: number;
}

interface PendingRename {
  name?: string;
  desc?: string;
}

interface PendingReorder {
  parentUid?: string;
  newSortId?: number;
}

type CoEventOp = 'update' | 'delete' | 'reorder' | 'create';

@Injectable()
export class CoStructureSyncService implements OnDestroy {

  private static readonly TASK_NAME_PATTERN = /^Export for Project Structure ([0-9a-f-]+) for schema ([0-9a-f-]+)$/i;

  private readonly pendingShells = new Map<string, PendingShell>();
  private readonly pendingRenames = new Map<string, PendingRename>();
  private readonly pendingDeletes = new Set<string>();
  private readonly pendingReorders = new Map<string, PendingReorder>();
  private readonly uidToExternalId = new Map<string, string>();

  private readonly errorsSubject = new Subject<StructureSyncError>();
  public readonly errors$: Observable<StructureSyncError> = this.errorsSubject.asObservable();

  private readonly subscription = new Subscription();

  constructor(
    private readonly taskService: TaskService,
    private readonly structureService: StructureService,
    private readonly coEventService: CoEventService,
  ) {
    this.taskService.observeTasks([TaskScopeEnum.user, TaskScopeEnum.client, TaskScopeEnum.customer]);

    const tasks$ = merge(this.taskService.onTaskStarted, this.taskService.onTaskChanged).pipe(share());

    this.subscription.add(
      tasks$.pipe(filter(t => this.isMatchingTask(t))).subscribe(t => {
        try {
          this.handleTask(t);
        } finally {
          this.coEventService.emit(CoEvent.StructureSynced, null);
        }
      })
    );
    this.subscription.add(
      tasks$.pipe(filter(t => this.isFailedTask(t))).subscribe(t => this.handleFailedTask(t))
    );

    const coBindings: Array<[CoEventOp, Observable<StructureEventData | StructureReorderEventData>]> = [
      ['create', this.coEventService.onStructureCreated],
      ['update', this.coEventService.onStructureUpdated],
      ['delete', this.coEventService.onStructureDeleted],
      ['reorder', this.coEventService.onStructureReordered as Observable<StructureEventData | StructureReorderEventData>],
    ];
    coBindings.forEach(([op, obs]) => this.subscription.add(obs.subscribe(event => this.handleCoEvent(op, event))));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private isMatchingTask(task: Task): boolean {
    return !!task?.name
      && CoStructureSyncService.TASK_NAME_PATTERN.test(task.name)
      && task?.status?.value === TaskStatusEnum.ended;
  }

  private isFailedTask(task: Task): boolean {
    return !!task?.name
      && CoStructureSyncService.TASK_NAME_PATTERN.test(task.name)
      && task?.status?.value === TaskStatusEnum.failed;
  }

  private handleCoEvent(op: CoEventOp, event: StructureEventData | StructureReorderEventData): void {
    const node = event?.node;
    if (!node?.id) return;

    if (op === 'create') {
      this.pendingShells.set(node.id, {
        uid: node.id,
        parentUid: node.parentId,
        name: node.name,
        desc: node.desc,
        level: node.level,
        sortId: node.sortId,
      });
      return;
    }

    if (op === 'update') {
      const shell = this.pendingShells.get(node.id);
      if (shell) {
        if (node.name !== undefined) shell.name = node.name;
        if (node.desc !== undefined) shell.desc = node.desc;
        return;
      }
      const applied = this.structureService.applyGridTreeUpdate({ myDocmaUid: node.id, name: node.name, desc: node.desc });
      if (!applied) {
        this.pendingRenames.set(node.id, { name: node.name, desc: node.desc });
        this.applyByCachedExternalId(node.id);
      }
      return;
    }

    if (op === 'delete') {
      if (this.pendingShells.delete(node.id)) return;
      const applied = this.structureService.applyGridTreeDelete({ myDocmaUid: node.id });
      if (!applied) {
        this.pendingDeletes.add(node.id);
        this.applyByCachedExternalId(node.id);
      }
      return;
    }

    if (op === 'reorder') {
      const reorderEvent = event as StructureReorderEventData;
      const shell = this.pendingShells.get(node.id);
      if (shell) {
        shell.parentUid = reorderEvent.parentId ?? shell.parentUid;
        shell.sortId = reorderEvent.newSortId;
        return;
      }
      const applied = this.structureService.applyGridTreeReorder({ myDocmaUid: node.id, parentId: reorderEvent.parentId, newSortId: reorderEvent.newSortId });
      if (!applied) {
        this.pendingReorders.set(node.id, { parentUid: reorderEvent.parentId, newSortId: reorderEvent.newSortId });
        this.applyByCachedExternalId(node.id);
      }
    }
  }

  private applyByCachedExternalId(uid: string): boolean {
    const externalId = this.uidToExternalId.get(uid);
    if (!externalId) return false;
    return this.drainPendingByExternalId(uid, externalId);
  }

  private drainPendingByExternalId(uid: string, externalId: string): boolean {
    let drained = false;
    const rename = this.pendingRenames.get(uid);
    if (rename) {
      this.structureService.applyGridTreeUpdate({ id: externalId, myDocmaUid: uid, name: rename.name, desc: rename.desc });
      this.pendingRenames.delete(uid);
      drained = true;
    }
    if (this.pendingDeletes.has(uid)) {
      this.structureService.applyGridTreeDelete({ id: externalId, myDocmaUid: uid });
      this.pendingDeletes.delete(uid);
      drained = true;
    }
    const reorder = this.pendingReorders.get(uid);
    if (reorder) {
      this.structureService.applyGridTreeReorder({ id: externalId, myDocmaUid: uid, parentId: reorder.parentUid, newSortId: reorder.newSortId });
      this.pendingReorders.delete(uid);
      drained = true;
    }
    return drained;
  }

  private handleTask(task: Task): void {
    try {
      const payload = this.extractPayload(task);
      if (!payload) {
        this.notifyResolutionFailure('Backend task payload missing or unsupported.', { taskName: task?.name });
        return;
      }

      const projectStructureId = payload.projectStructureId;
      const numericId = this.toPositiveNumber(payload.externalId ?? payload.structureId);

      if (!projectStructureId) {
        this.notifyResolutionFailure('Backend task payload missing projectStructureId.', { taskName: task?.name, payload });
        return;
      }
      if (!numericId) {
        this.notifyResolutionFailure('Backend task payload missing usable numeric id.', { uid: projectStructureId, payload });
        return;
      }

      const numericIdStr = String(numericId);
      this.uidToExternalId.set(projectStructureId, numericIdStr);

      const shell = this.pendingShells.get(projectStructureId);
      if (shell) {
        this.pendingShells.delete(projectStructureId);
        const consumerRoot = this.structureService.structuresTree;
        const parentArg = shell.level === 1 && consumerRoot?.id ? consumerRoot.id : shell.parentUid;
        const inserted = this.structureService.applyGridTreeCreate({
          id: numericIdStr,
          myDocmaUid: projectStructureId,
          name: shell.name ?? '',
          desc: shell.desc ?? '',
          parentId: parentArg,
          level: shell.level,
          sortId: shell.sortId,
        });
        return;
      }

      this.drainPendingByExternalId(projectStructureId, numericIdStr);
    } catch (e) {
      console.error('[CO-SYNC] task handler failed', e);
    }
  }

  private handleFailedTask(task: Task): void {
    const failureMessage = this.getFailureMessage(task);
    const match = task?.name?.match(CoStructureSyncService.TASK_NAME_PATTERN);
    const uidFromName = match?.[1];
    let droppedShell = false;
    let droppedRename = false;
    let droppedDelete = false;
    let droppedReorder = false;
    if (uidFromName) {
      droppedShell = this.pendingShells.delete(uidFromName);
      droppedRename = this.pendingRenames.delete(uidFromName);
      droppedDelete = this.pendingDeletes.delete(uidFromName);
      droppedReorder = this.pendingReorders.delete(uidFromName);
    }
    this.errorsSubject.next({
      kind: StructureSyncErrorKind.BackendFailed,
      reason: failureMessage ?? undefined,
      info: { name: task?.name, taskId: (task as any)?.id, droppedShell, droppedRename, droppedDelete, droppedReorder },
    });
  }

  private extractPayload(task: Task): BackendTaskPayload | null {
    const directData = (task as any)?.data;
    if (directData && typeof directData === 'object' && !Array.isArray(directData)) {
      return directData as BackendTaskPayload;
    }
    const statusData = (task.status?.data as any)?.data;
    if (typeof statusData === 'string') {
      try { return JSON.parse(statusData) as BackendTaskPayload; } catch { return null; }
    }
    if (statusData && typeof statusData === 'object' && !Array.isArray(statusData)) {
      return statusData as BackendTaskPayload;
    }
    return null;
  }

  private getFailureMessage(task: Task): string | null {
    const log = (task as any)?.statusLog;
    if (!Array.isArray(log)) return null;
    for (let i = log.length - 1; i >= 0; i--) {
      const text = log[i]?.data?.data;
      if (typeof text === 'string' && text.trim()) return text.trim();
    }
    return null;
  }

  private toPositiveNumber(value: unknown): number | null {
    const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private notifyResolutionFailure(message: string, info: Record<string, unknown>): void {
    console.error('[CO-SYNC-RESOLVE-FAIL]', message, info);
    this.errorsSubject.next({ kind: StructureSyncErrorKind.ResolutionFailed, reason: message, info });
  }
}
